import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Hash, Clock, DollarSign, AlertTriangle, Trophy, TrendingUp, TrendingDown, Eye, Dice1 } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Digit selector wheel
const DigitWheel = ({ value, onChange, revealed, revealedDigit }) => {
  const digits = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  return (
    <div className="relative flex flex-col items-center">
      {revealed && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute -top-5 px-1.5 py-0.5 bg-[#C9A84C]/20 border border-[#C9A84C]/40 rounded text-[7px] font-['Outfit'] text-[#C9A84C] tracking-widest whitespace-nowrap z-10"
        >
          INSIDER TIP
        </motion.div>
      )}
      <div className="relative w-14 h-20 overflow-hidden rounded-lg border border-[#1A2535] bg-[#0A0E14]">
        <div className="absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-[#0A0E14] to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-[#0A0E14] to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-8 border-t border-b border-[#C9A84C]/30 z-10 pointer-events-none" />
        <div
          className="transition-transform duration-200"
          style={{ transform: `translateY(${-value * 20 + 30}px)` }}
        >
          {digits.map(d => (
            <button
              key={d}
              onClick={() => onChange(d)}
              className={`w-full h-5 flex items-center justify-center text-base font-['Outfit'] font-bold transition-colors ${
                d === value
                  ? revealed ? 'text-[#C9A84C]' : 'text-[#E8E0D0]'
                  : 'text-[#E8E0D0]/15 hover:text-[#E8E0D0]/30'
              }`}
              data-testid={`digit-${d}`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>
      <button
        onClick={() => onChange(value >= 9 ? 0 : value + 1)}
        className="mt-1 w-6 h-6 flex items-center justify-center text-[#E8E0D0]/20 hover:text-[#C9A84C] transition-colors"
      >
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
      </button>
    </div>
  );
};

// Result display with animation
const DrawResult = ({ result, onClose }) => {
  if (!result) return null;

  const isWin = result.result === 'win';
  const isBooked = result.result === 'runner_booked';
  const isRobbed = result.result === 'runner_robbed';
  const isRunnerIncident = isBooked || isRobbed;

  const bgColor = isWin ? 'bg-[#4ADE80]/5' : isRunnerIncident ? 'bg-[#C9A84C]/5' : 'bg-[#8B1A1A]/5';
  const borderColor = isWin ? 'border-[#4ADE80]/30' : isRunnerIncident ? 'border-[#C9A84C]/30' : 'border-[#8B1A1A]/20';
  const accentColor = isWin ? '#4ADE80' : isRunnerIncident ? '#C9A84C' : '#8B1A1A';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`${bgColor} border ${borderColor} rounded-xl p-5 text-center relative`}
    >
      {/* Winning number reveal */}
      <div className="mb-4">
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-[0.3em] mb-2">WINNING NUMBER</p>
        <div className="flex items-center justify-center gap-2">
          {result.winning_number.split('').map((d, i) => {
            const playerDigit = result.player_number[i];
            const match = d === playerDigit;
            return (
              <motion.div
                key={i}
                initial={{ rotateX: 90, opacity: 0 }}
                animate={{ rotateX: 0, opacity: 1 }}
                transition={{ delay: i * 0.3, duration: 0.4 }}
                className={`w-12 h-14 flex items-center justify-center rounded-lg border text-xl font-['Outfit'] font-bold ${
                  match ? 'border-[#4ADE80]/50 bg-[#4ADE80]/10 text-[#4ADE80]' : 'border-[#1A2535] bg-[#0A0E14] text-[#E8E0D0]/60'
                }`}
              >
                {d}
              </motion.div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-2 mt-2">
          <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">YOUR NUMBER:</span>
          <span className="font-['Outfit'] text-[#E8E0D0]/50 text-sm tracking-[0.2em]">{result.player_number}</span>
        </div>
      </div>

      {/* Result status */}
      {isRunnerIncident && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mb-3 px-3 py-2 bg-[#C9A84C]/10 border border-[#C9A84C]/20 rounded-lg"
        >
          <p className="font-['Outfit'] text-[#C9A84C] text-xs font-semibold mb-0.5">
            {isBooked ? 'RUNNER GOT BOOKED' : 'RUNNER GOT ROBBED'}
          </p>
          <p className="font-['Outfit'] text-[#C9A84C]/60 text-[10px]">
            {isBooked ? 'Cops grabbed your man with the slip. Money\'s gone.' : 'Somebody ran up on your runner. Took everything.'}
          </p>
        </motion.div>
      )}

      {/* Payout */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        {isWin ? (
          <div>
            <p className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#4ADE80] mb-1">
              +${result.payout?.toLocaleString()}
            </p>
            <p className="font-['Outfit'] text-[#4ADE80]/60 text-[10px]">
              {result.bet_type === 'straight' ? 'STRAIGHT HIT — 500:1' : 'BOX HIT'} on ${result.bet_amount?.toLocaleString()} bet
            </p>
          </div>
        ) : (
          <div>
            <p className="font-['Outfit'] text-sm" style={{ color: accentColor }}>
              -${result.bet_amount?.toLocaleString()}
            </p>
          </div>
        )}
      </motion.div>

      {/* Narrator */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="font-['Cormorant_Garamond'] text-sm italic text-[#E8E0D0]/40 mt-4 leading-relaxed"
      >
        "{result.narrator}"
      </motion.p>

      <button
        onClick={onClose}
        className="mt-4 px-4 py-1.5 bg-[#1A2535] border border-[#1A2535] rounded text-[#E8E0D0]/40 font-['Outfit'] text-[9px] tracking-widest hover:text-[#E8E0D0] transition-colors"
        data-testid="numbers-result-close"
      >
        CLOSE
      </button>
    </motion.div>
  );
};

// =============================================
// MAIN NUMBERS GAME COMPONENT
// =============================================
const NumbersGame = ({ gameId, onRefresh }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digit1, setDigit1] = useState(0);
  const [digit2, setDigit2] = useState(0);
  const [digit3, setDigit3] = useState(0);
  const [betType, setBetType] = useState('straight');
  const [betAmount, setBetAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [gettingTip, setGettingTip] = useState(false);
  const [drawResult, setDrawResult] = useState(null);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const timerRef = useRef(null);
  const [countdown, setCountdown] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/game/${gameId}/numbers/status`);
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        if (!betAmount && data.bet_limits) {
          setBetAmount(String(data.bet_limits.min));
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [gameId, betAmount]);

  useEffect(() => { load(); }, [load]);

  // Countdown timer
  useEffect(() => {
    if (!status) return;
    const update = () => {
      const target = status.before_cutoff ? status.seconds_to_cutoff : status.seconds_to_draw;
      const now = Date.now() / 1000;
      const elapsed = now - (Date.now() / 1000 - target);
      const remaining = Math.max(0, Math.floor(target - (Date.now() / 1000 - now)));
      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;
      setCountdown(`${h}h ${m}m ${s}s`);
    };
    update();
    timerRef.current = setInterval(update, 1000);
    return () => clearInterval(timerRef.current);
  }, [status]);

  const placeBet = async () => {
    setError(null);
    setPlacing(true);
    const number = `${digit1}${digit2}${digit3}`;
    try {
      const res = await fetch(`${API}/api/game/${gameId}/numbers/bet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ number, bet_type: betType, amount: parseInt(betAmount) }),
      });
      const data = await res.json();
      if (res.ok) {
        load();
        onRefresh();
      } else {
        setError(data.detail);
        setTimeout(() => setError(null), 4000);
      }
    } catch (e) { setError('Network error'); }
    setPlacing(false);
  };

  const checkDraw = async () => {
    setDrawing(true);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/numbers/draw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        setDrawResult(data);
        onRefresh();
      } else {
        setError(data.detail);
        setTimeout(() => setError(null), 4000);
      }
    } catch (e) { setError('Network error'); }
    setDrawing(false);
  };

  const getInsiderTip = async () => {
    setGettingTip(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/numbers/insider-tip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        load();
        onRefresh();
      } else {
        setError(data.detail);
        setTimeout(() => setError(null), 4000);
      }
    } catch (e) { setError('Network error'); }
    setGettingTip(false);
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API}/api/game/${gameId}/numbers/history`);
      if (res.ok) setHistoryData(await res.json());
    } catch (e) { console.error(e); }
    setShowHistory(true);
  };

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs text-center py-8">Loading...</p>;
  if (!status) return null;

  // If we're showing the draw result overlay
  if (drawResult) {
    return (
      <DrawResult
        result={drawResult}
        onClose={() => { setDrawResult(null); load(); }}
      />
    );
  }

  // History view
  if (showHistory && historyData) {
    return (
      <div className="space-y-3">
        <button
          onClick={() => setShowHistory(false)}
          className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C] transition-colors"
          data-testid="numbers-history-back"
        >
          BACK
        </button>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-[#0A0E14] border border-[#1A2535] rounded-lg p-2.5 text-center">
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-widest">BETS</p>
            <p className="font-['Outfit'] text-[#E8E0D0] text-sm font-bold">{historyData.stats.total_bets}</p>
          </div>
          <div className="bg-[#0A0E14] border border-[#1A2535] rounded-lg p-2.5 text-center">
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-widest">WIN RATE</p>
            <p className="font-['Outfit'] text-[#C9A84C] text-sm font-bold">{historyData.stats.win_rate}%</p>
          </div>
          <div className="bg-[#0A0E14] border border-[#1A2535] rounded-lg p-2.5 text-center">
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-widest">NET</p>
            <p className={`font-['Outfit'] text-sm font-bold ${historyData.stats.net >= 0 ? 'text-[#4ADE80]' : 'text-[#8B1A1A]'}`}>
              {historyData.stats.net >= 0 ? '+' : ''}${historyData.stats.net?.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-widest">RUNNER INCIDENTS: {historyData.stats.runner_incidents}</span>
          <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-widest">WAGERED: ${historyData.stats.total_wagered?.toLocaleString()}</span>
        </div>

        {/* History list */}
        <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
          {historyData.history?.slice().reverse().map((h, i) => (
            <div
              key={i}
              className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs font-['Outfit'] ${
                h.result === 'win'
                  ? 'bg-[#4ADE80]/5 border-[#4ADE80]/15'
                  : h.result === 'runner_booked' || h.result === 'runner_robbed'
                  ? 'bg-[#C9A84C]/5 border-[#C9A84C]/15'
                  : 'bg-[#0A0E14] border-[#1A2535]'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-[#E8E0D0]/50 font-mono">{h.number}</span>
                <span className="text-[#E8E0D0]/20 text-[8px] uppercase">{h.bet_type}</span>
                {(h.result === 'runner_booked' || h.result === 'runner_robbed') && (
                  <AlertTriangle size={10} className="text-[#C9A84C]/60" />
                )}
              </div>
              <div className="text-right">
                <span className={h.result === 'win' ? 'text-[#4ADE80]' : 'text-[#8B1A1A]/60'}>
                  {h.result === 'win' ? `+$${h.payout?.toLocaleString()}` : `-$${h.amount?.toLocaleString()}`}
                </span>
                <p className="text-[#E8E0D0]/15 text-[7px]">{h.draw_date} | drew {h.winning_number}</p>
              </div>
            </div>
          ))}
          {(!historyData.history || historyData.history.length === 0) && (
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-4">No history yet. Play your first number.</p>
          )}
        </div>
      </div>
    );
  }

  const hasBetToday = status.current_bet && status.current_bet.draw_date === status.draw_date;
  const tip = status.insider_tip;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h4 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#E8E0D0] tracking-wide" data-testid="numbers-title">
          THE NUMBERS
        </h4>
        <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[10px] tracking-widest mt-0.5">
          {status.bet_limits?.label}
        </p>
      </div>

      {/* Timer bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#0A0E14] border border-[#1A2535] rounded-lg">
        <div className="flex items-center gap-1.5">
          <Clock size={10} className="text-[#E8E0D0]/20" />
          <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest">
            {status.before_cutoff ? 'BOOKS CLOSE IN' : 'NEXT DRAW IN'}
          </span>
        </div>
        <span className="font-['Outfit'] text-[#C9A84C] text-xs font-mono">{countdown}</span>
      </div>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="px-3 py-2 bg-[#8B1A1A]/10 border border-[#8B1A1A]/20 rounded-lg"
          >
            <p className="font-['Outfit'] text-[#8B1A1A] text-xs">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pending result — check draw button */}
      {status.has_pending_result && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-4"
        >
          <div className="mb-3">
            <p className="font-['Outfit'] text-[#C9A84C]/60 text-[10px] tracking-widest mb-1">YOUR NUMBER IS IN</p>
            <div className="flex items-center justify-center gap-1.5">
              {status.current_bet?.number.split('').map((d, i) => (
                <div key={i} className="w-10 h-12 flex items-center justify-center rounded-lg border border-[#C9A84C]/30 bg-[#C9A84C]/5 text-[#C9A84C] font-['Outfit'] font-bold text-lg">
                  {d}
                </div>
              ))}
            </div>
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] mt-1">
              {status.current_bet?.bet_type?.toUpperCase()} — ${status.current_bet?.amount?.toLocaleString()}
            </p>
          </div>

          <button
            onClick={checkDraw}
            disabled={drawing}
            className="px-6 py-3 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded-lg text-[#C9A84C] font-['Outfit'] text-xs tracking-[0.2em] hover:bg-[#C9A84C]/20 disabled:opacity-30 transition-all animate-pulse"
            data-testid="numbers-check-draw"
          >
            {drawing ? 'CHECKING...' : 'CHECK THE DRAW'}
          </button>
        </motion.div>
      )}

      {/* Active bet waiting for draw */}
      {hasBetToday && !status.has_pending_result && (
        <div className="text-center py-4">
          <div className="mb-3">
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest mb-1">BET PLACED FOR TODAY</p>
            <div className="flex items-center justify-center gap-1.5">
              {status.current_bet?.number.split('').map((d, i) => (
                <div key={i} className="w-10 h-12 flex items-center justify-center rounded-lg border border-[#C9A84C]/20 bg-[#0A0E14] text-[#E8E0D0] font-['Outfit'] font-bold text-lg">
                  {d}
                </div>
              ))}
            </div>
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] mt-1">
              {status.current_bet?.bet_type?.toUpperCase()} — ${status.current_bet?.amount?.toLocaleString()}
            </p>
          </div>
          <p className="font-['Cormorant_Garamond'] text-sm italic text-[#E8E0D0]/25">
            "Your runner's got the slip. Now we wait for the draw."
          </p>
        </div>
      )}

      {/* Bet form — only if can bet */}
      {status.can_bet && (
        <div className="space-y-4">
          {/* Number picker */}
          <div>
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-[0.3em] mb-3 text-center">PICK YOUR NUMBER</p>
            <div className="flex items-center justify-center gap-3">
              <DigitWheel
                value={digit1}
                onChange={setDigit1}
                revealed={tip?.position === 0}
                revealedDigit={tip?.digit}
              />
              <DigitWheel
                value={digit2}
                onChange={setDigit2}
                revealed={tip?.position === 1}
                revealedDigit={tip?.digit}
              />
              <DigitWheel
                value={digit3}
                onChange={setDigit3}
                revealed={tip?.position === 2}
                revealedDigit={tip?.digit}
              />
            </div>
            <p className="font-['Outfit'] text-[#E8E0D0]/50 text-center text-sm mt-2 font-mono tracking-[0.3em]">
              {digit1}{digit2}{digit3}
            </p>
          </div>

          {/* Insider tip button */}
          {!tip && status.before_cutoff && (
            <button
              onClick={getInsiderTip}
              disabled={gettingTip}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#C9A84C]/5 border border-[#C9A84C]/15 rounded-lg text-[#C9A84C]/60 font-['Outfit'] text-[9px] tracking-widest hover:bg-[#C9A84C]/10 hover:text-[#C9A84C] disabled:opacity-30 transition-all"
              data-testid="numbers-insider-tip"
            >
              <Eye size={12} />
              {gettingTip ? 'GETTING WORD...' : 'INSIDER TIP (5 ENERGY)'}
            </button>
          )}

          {/* Insider tip display */}
          {tip && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="px-3 py-2 bg-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-lg text-center"
            >
              <p className="font-['Outfit'] text-[#C9A84C]/80 text-[10px]">
                Word is the <span className="font-bold text-[#C9A84C]">{tip.position_label}</span> digit is{' '}
                <span className="font-bold text-[#C9A84C] text-sm">{tip.digit}</span>
              </p>
            </motion.div>
          )}

          {/* Bet type toggle */}
          <div>
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-[0.3em] mb-2">BET TYPE</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setBetType('straight')}
                className={`px-3 py-2.5 rounded-lg border font-['Outfit'] text-xs transition-all ${
                  betType === 'straight'
                    ? 'bg-[#C9A84C]/10 border-[#C9A84C]/40 text-[#C9A84C]'
                    : 'border-[#1A2535] text-[#E8E0D0]/30 hover:border-[#E8E0D0]/15'
                }`}
                data-testid="numbers-bet-straight"
              >
                <span className="font-semibold">STRAIGHT</span>
                <p className="text-[8px] mt-0.5 opacity-60">Exact match — 500:1</p>
              </button>
              <button
                onClick={() => setBetType('box')}
                className={`px-3 py-2.5 rounded-lg border font-['Outfit'] text-xs transition-all ${
                  betType === 'box'
                    ? 'bg-[#C9A84C]/10 border-[#C9A84C]/40 text-[#C9A84C]'
                    : 'border-[#1A2535] text-[#E8E0D0]/30 hover:border-[#E8E0D0]/15'
                }`}
                data-testid="numbers-bet-box"
              >
                <span className="font-semibold">BOX</span>
                <p className="text-[8px] mt-0.5 opacity-60">Any order — 80-160:1</p>
              </button>
            </div>
          </div>

          {/* Bet amount */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] tracking-[0.3em]">BET AMOUNT</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[8px]">
                ${status.bet_limits?.min?.toLocaleString()} - ${status.bet_limits?.max?.toLocaleString()}
              </p>
            </div>
            <div className="relative">
              <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#E8E0D0]/20" />
              <input
                type="number"
                value={betAmount}
                onChange={e => setBetAmount(e.target.value)}
                min={status.bet_limits?.min}
                max={status.bet_limits?.max}
                className="w-full pl-8 pr-3 py-2.5 bg-[#0A0E14] border border-[#1A2535] rounded-lg text-[#E8E0D0] font-['Outfit'] text-sm focus:border-[#C9A84C]/40 outline-none transition-colors"
                data-testid="numbers-bet-amount"
              />
            </div>
            {/* Quick amounts */}
            <div className="flex gap-1.5 mt-2">
              {[status.bet_limits?.min, Math.floor((status.bet_limits?.max - status.bet_limits?.min) * 0.25 + status.bet_limits?.min), Math.floor((status.bet_limits?.max - status.bet_limits?.min) * 0.5 + status.bet_limits?.min), status.bet_limits?.max].map((amt, i) => (
                <button
                  key={i}
                  onClick={() => setBetAmount(String(amt))}
                  className="flex-1 py-1 bg-[#1A2535]/50 border border-[#1A2535] rounded text-[#E8E0D0]/30 font-['Outfit'] text-[8px] hover:text-[#C9A84C] hover:border-[#C9A84C]/20 transition-colors"
                  data-testid={`numbers-quick-bet-${i}`}
                >
                  ${amt?.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {/* Cash display */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-[#0A0E14] rounded-lg border border-[#1A2535]">
            <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">YOUR CASH</span>
            <span className="font-['Outfit'] text-[#4ADE80] text-xs">${status.cash?.toLocaleString()}</span>
          </div>

          {/* Place bet button */}
          <button
            onClick={placeBet}
            disabled={placing || !betAmount || parseInt(betAmount) < status.bet_limits?.min || parseInt(betAmount) > status.bet_limits?.max || parseInt(betAmount) > status.cash}
            className="w-full py-3 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded-lg text-[#C9A84C] font-['Outfit'] text-xs tracking-[0.2em] hover:bg-[#C9A84C]/20 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
            data-testid="numbers-place-bet"
          >
            {placing ? 'SENDING RUNNER...' : `PLAY ${digit1}${digit2}${digit3} — $${parseInt(betAmount || 0).toLocaleString()} ${betType.toUpperCase()}`}
          </button>

          {/* Runner risk warning */}
          <div className="flex items-start gap-2 px-3 py-2 bg-[#8B1A1A]/5 border border-[#8B1A1A]/10 rounded-lg">
            <AlertTriangle size={12} className="text-[#8B1A1A]/30 shrink-0 mt-0.5" />
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] leading-relaxed">
              Your runner carries the slip to the bookie. ~20% chance the runner gets <span className="text-[#8B1A1A]/50">booked</span> or <span className="text-[#8B1A1A]/50">robbed</span> — you lose your bet even if the number hits.
            </p>
          </div>
        </div>
      )}

      {/* Can't bet — books closed */}
      {!status.can_bet && !hasBetToday && !status.has_pending_result && (
        <div className="text-center py-4">
          <p className="font-['Cormorant_Garamond'] text-sm italic text-[#E8E0D0]/25">
            "Books are closed. Come back before 11 PM to get your number in."
          </p>
        </div>
      )}

      {/* Streak + History */}
      <div className="flex items-center justify-between">
        {status.streak > 0 && (
          <span className="font-['Outfit'] text-[#C9A84C]/30 text-[9px] tracking-widest">
            {status.streak} DAY STREAK
          </span>
        )}
        <button
          onClick={loadHistory}
          className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest hover:text-[#C9A84C] transition-colors ml-auto"
          data-testid="numbers-view-history"
        >
          VIEW HISTORY
        </button>
      </div>
    </div>
  );
};

export default NumbersGame;
